import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { sendAliasVerification, useCreateUserAlias, useReadUserProfile } from '@/api/user-manager'
import {

  Box,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { isValidPhone, removeNonNumeric, toast } from '@/helpers'
import { useState } from 'react'
import { VerificationCode } from './verification-code'
import { MaskedInput } from '@/components'
import { queryClient } from '@/api/react-query'

const phoneFormSchema = z.object({
  phone: z
    .string()
    .min(1, 'Obrigatório')
    .refine((value) => isValidPhone(value), 'Telefone inválido'),
})

type PhoneFormData = z.infer<typeof phoneFormSchema>

export function PhoneForm() {
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)

  const { data: user } = useReadUserProfile()

  const initialPhone = user?.alias?.find(
    (alias) => alias.type === 'phone',
  )

  const initialPhoneData = initialPhone?.data
  const initialPhoneVerified = initialPhone?.verified

  const { mutateAsync: createUserAlias, isPending: creatingUserAlias } =
    useCreateUserAlias({
      onSuccess: () => handleOpenVerificationDialog(),
    })

  const { control, handleSubmit, watch } = useForm<PhoneFormData>({
    resolver: zodResolver(phoneFormSchema),
    values: {
      phone: initialPhoneData || '',
    },
  })

  const phoneField = watch('phone')

  const isPhoneFieldAndUserPhoneEqual = removeNonNumeric(phoneField) === removeNonNumeric(initialPhoneData || '')

  const handleOpenVerificationDialog = () => {
    sendAliasVerification({
      alias: phoneField,
      alias_type: 'phone',
    })

    setVerificationDialogOpen(true)
  }

  const handleCloseVerificationDialog = () => setVerificationDialogOpen(false)

  const handleSuccessVerificationCode = () => {
    queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    toast.success('Telefone verificado com sucesso!')
    handleCloseVerificationDialog()
  }

  function handleUpdatePhone({ phone }: PhoneFormData) {
    createUserAlias({
      data: phone,
      type: 'phone',
    })
  }

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(handleUpdatePhone)}>
        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MaskedInput
              {...field}
              label="Telefone"
              type="tel"
              mask="(00) 00000-0000"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <LoadingButton
          type="submit"
          color={initialPhoneVerified ? 'primary' : 'warning'}
          sx={{ mt: 2 }}
          disabled={isPhoneFieldAndUserPhoneEqual && initialPhoneVerified}
          loading={creatingUserAlias}
        >
          {initialPhoneVerified ? 'Alterar Telefone' : 'Verificar Telefone'}
        </LoadingButton>
      </Box>

      <Dialog open={verificationDialogOpen} onClose={handleCloseVerificationDialog}>
        <DialogTitle textAlign="center">Verificação de telefone</DialogTitle>
        <DialogContent>
          <VerificationCode
            type="phone"
            data={phoneField}
            gap={1}
            maxWidth={265}
            margin="auto"
            onSuccess={handleSuccessVerificationCode}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
